import { defu } from 'defu'
import type { FetchContext, FetchHook, FetchOptions } from 'ofetch'
import { $fetch } from 'ofetch'
import { useWorkspace } from '~/stores/workspace'
import { makeTracingHeaders, useFetch, useNuxtApp } from '#imports'
import type { UseFetchOptions } from '#app'
import type { NitroFetchRequest } from 'nitropack'

const createDefaultOptions = <T = unknown>({
  onRequest,
  ...opts
}: FetchOptions = {}): FetchOptions => {
  const externalOnRequest = onRequest

  const addAuthHeaders: FetchHook<FetchContext<T>> = async ({ options }) => {
    const { $auth0 } = useNuxtApp()
    const { getAccessTokenSilently, isAuthenticated } = $auth0()
    const workspace = useWorkspace()

    let token = ''

    if (isAuthenticated.value) {
      token = await getAccessTokenSilently()
    }

    const headers = {
      Authorization: `Bearer ${token}`,
      'x-rialtic-workspace-id': workspace.workspaceId,
      'x-rialtic-connector-id': workspace.connectorId,
      workspace: workspace.workspaceId,
      'x-rialtic-impact-calculation-method':
        workspace.financialCalculationMethod,
      ...makeTracingHeaders(),
    }

    options.headers = defu(headers, options.headers)
  }

  if (!externalOnRequest) {
    return {
      ...opts,
      onRequest: [addAuthHeaders],
    }
  }

  return {
    ...opts,
    onRequest: [
      ...(Array.isArray(externalOnRequest)
        ? externalOnRequest
        : [externalOnRequest]),
      addAuthHeaders,
    ],
  }
}

export const $apiFetcher = () => {
  return <T>(url: string, options?: FetchOptions) =>
    $fetch<T>(url, createDefaultOptions(options) as FetchOptions<'json'>)
}

// Nuxt's useFetch supports null as a URL to skip the request at runtime
type NullableRequest = NitroFetchRequest | null
type UseApiFetchUrl =
  | NullableRequest
  | Ref<NullableRequest>
  | (() => NullableRequest)

/**
 * Wrapper around useFetch with default options for API calls
 * See: https://nuxt.com/docs/api/composables/use-fetch
 *
 * NOTE: do NOT wrap this in a function, this should be used at the top level of a component's setup
 */
export const useApiFetch = <ResT, DataT = ResT>(
  url: UseApiFetchUrl,
  options?: FetchOptions | UseFetchOptions<ResT, DataT>,
) => {
  return useFetch(
    url as NitroFetchRequest,
    createDefaultOptions(options as FetchOptions) as UseFetchOptions<
      ResT,
      DataT
    >,
  )
}
